/**
 * Idle / step / caption state for PageGuide.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  speakPageGuideText,
  stopPageGuideSpeech,
  unlockPageGuideAudio,
} from "./pageGuideAudio";
import {
  hasOtherVoiceSession,
  acquireVoiceSession,
  releaseVoiceSession,
} from "./voiceSessionRegistry";
import { usePageGuideContextOptional } from "./PageGuideContext";
import { fetchPageGuideConfig } from "services/pageGuide/pageGuideService";

const PAGE_GUIDE_SESSION_ID = "pageGuide";

function isPageVisible() {
  if (typeof document === "undefined") return true;
  return document.visibilityState === "visible";
}

function isStandalonePwa() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches === true ||
    window.navigator?.standalone === true
  );
}

/**
 * @param {{ pageId: string, facilityId?: string|null }} options
 */
export default function usePageGuide({ pageId, facilityId = null } = {}) {
  const ctx = usePageGuideContextOptional();
  const [config, setConfig] = useState(null);
  const [offered, setOffered] = useState(false);
  const [platformEnabled, setPlatformEnabled] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loading, setLoading] = useState(true);

  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [nudgeCount, setNudgeCount] = useState(0);
  const [caption, setCaption] = useState("");
  const [soundBlocked, setSoundBlocked] = useState(false);
  const [dismissedOnPage, setDismissedOnPage] = useState(false);
  const [helpAffordance, setHelpAffordance] = useState(false);
  const [micPrimingOpen, setMicPrimingOpen] = useState(false);
  const [talkBackActive, setTalkBackActive] = useState(false);
  const [talkBackError, setTalkBackError] = useState("");
  const [textFallbackOpen, setTextFallbackOpen] = useState(false);
  const [iosHeadphoneWarning, setIosHeadphoneWarning] = useState(false);

  const idleTimerRef = useRef(null);
  const lastInteractionRef = useRef(Date.now());
  const speakingRef = useRef(false);
  const nudgeCountRef = useRef(0);
  const activeStepIndexRef = useRef(0);
  const dismissedRef = useRef(false);
  const configRef = useRef(null);
  const visibleRef = useRef(true);
  const introPlayingRef = useRef(false);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;

  useEffect(() => {
    nudgeCountRef.current = nudgeCount;
  }, [nudgeCount]);
  useEffect(() => {
    activeStepIndexRef.current = activeStepIndex;
  }, [activeStepIndex]);
  useEffect(() => {
    dismissedRef.current = dismissedOnPage;
  }, [dismissedOnPage]);
  useEffect(() => {
    configRef.current = config;
  }, [config]);

  const userEnabled = ctx?.userEnabled === true;
  const hasCoachingSteps =
    Array.isArray(config?.steps) && config.steps.length > 0;
  const masterOk =
    platformEnabled &&
    offered &&
    userEnabled &&
    !dismissedOnPage &&
    Boolean(config) &&
    hasCoachingSteps;

  const introductionText =
    typeof config?.introductionText === "string"
      ? config.introductionText.trim()
      : "";

  // Load config
  useEffect(() => {
    let cancelled = false;
    if (!pageId) {
      setLoading(false);
      setOffered(false);
      setConfig(null);
      ctxRef.current?.reportPageOffered?.(false);
      ctxRef.current?.reportIntroduction?.(false);
      return undefined;
    }

    setLoading(true);
    setLoadError(null);

    fetchPageGuideConfig(pageId, facilityId)
      .then((result) => {
        if (cancelled) return;
        const pe = result.platformEnabled !== false;
        setPlatformEnabled(pe);
        if (!pe) {
          setOffered(false);
          setConfig(null);
          ctxRef.current?.reportPageOffered?.(false);
          ctxRef.current?.reportIntroduction?.(false);
          return;
        }
        if (!result.offered || !result.config) {
          setOffered(false);
          setConfig(null);
          ctxRef.current?.reportPageOffered?.(false);
          ctxRef.current?.reportIntroduction?.(false);
          return;
        }
        const intro =
          typeof result.config.introductionText === "string"
            ? result.config.introductionText.trim()
            : "";
        setOffered(true);
        setConfig(result.config);
        // Header icon is selectable only when an introduction exists.
        ctxRef.current?.reportPageOffered?.(Boolean(intro));
        ctxRef.current?.reportIntroduction?.(Boolean(intro));
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[PageGuide] config load failed", err);
        setLoadError(err);
        setOffered(false);
        setConfig(null);
        ctxRef.current?.reportPageOffered?.(false);
        ctxRef.current?.reportIntroduction?.(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      ctxRef.current?.reportPageOffered?.(false);
      ctxRef.current?.reportIntroduction?.(false);
      ctxRef.current?.setIntroPlaying?.(false);
      ctxRef.current?.registerIntroControllers?.({ play: null, stop: null });
    };
  }, [pageId, facilityId]);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const stopAudio = useCallback(() => {
    stopPageGuideSpeech();
    speakingRef.current = false;
    if (introPlayingRef.current) {
      introPlayingRef.current = false;
      ctxRef.current?.setIntroPlaying?.(false);
    }
  }, []);

  const stopIntroduction = useCallback(() => {
    stopAudio();
    setCaption("");
  }, [stopAudio]);

  const playIntroduction = useCallback(async () => {
    const text =
      typeof configRef.current?.introductionText === "string"
        ? configRef.current.introductionText.trim()
        : "";
    if (!text || !isPageVisible()) return;

    if (introPlayingRef.current) {
      stopIntroduction();
      return;
    }

    if (hasOtherVoiceSession(PAGE_GUIDE_SESSION_ID)) {
      setCaption(
        "PageGuide is paused while another voice feature is active. Tap again later.",
      );
      return;
    }
    if (!acquireVoiceSession(PAGE_GUIDE_SESSION_ID)) {
      setCaption(
        "PageGuide is paused while another voice feature is active. Tap again later.",
      );
      return;
    }

    unlockPageGuideAudio();
    introPlayingRef.current = true;
    ctxRef.current?.setIntroPlaying?.(true);
    setCaption(`${text}\n\nTap the PageGuide icon to stop.`);
    speakingRef.current = true;
    try {
      const result = await speakPageGuideText(text, {
        onBlocked: () => setSoundBlocked(true),
        onError: () => {
          /* caption already visible */
        },
      });
      if (result === "blocked") setSoundBlocked(true);
    } finally {
      speakingRef.current = false;
      introPlayingRef.current = false;
      ctxRef.current?.setIntroPlaying?.(false);
      releaseVoiceSession(PAGE_GUIDE_SESSION_ID);
    }
  }, [stopIntroduction]);

  useEffect(() => {
    const register = ctxRef.current?.registerIntroControllers;
    if (!register) return undefined;
    if (!introductionText) {
      register({ play: null, stop: null });
      return undefined;
    }
    register({
      play: playIntroduction,
      stop: stopIntroduction,
    });
    return () => {
      register({ play: null, stop: null });
    };
  }, [introductionText, playIntroduction, stopIntroduction]);
  const getActiveStep = useCallback(() => {
    const steps = configRef.current?.steps;
    if (!Array.isArray(steps) || steps.length === 0) return null;
    return steps[activeStepIndexRef.current] || null;
  }, []);

  const playNudge = useCallback(
    async (text) => {
      if (!text || !isPageVisible()) return;
      if (hasOtherVoiceSession(PAGE_GUIDE_SESSION_ID)) return;
      if (!acquireVoiceSession(PAGE_GUIDE_SESSION_ID)) return;

      setCaption(text);
      speakingRef.current = true;
      const result = await speakPageGuideText(text, {
        onBlocked: () => setSoundBlocked(true),
        onError: () => {
          /* caption already visible */
        },
      });
      speakingRef.current = false;
      releaseVoiceSession(PAGE_GUIDE_SESSION_ID);
      if (result === "blocked") setSoundBlocked(true);
    },
    [],
  );

  const scheduleNextIdle = useCallback(() => {
    clearIdleTimer();
    if (!masterOk || !visibleRef.current) return;
    if (talkBackActive) return;
    if (hasOtherVoiceSession(PAGE_GUIDE_SESSION_ID)) return;

    const step = getActiveStep();
    if (!step) return;

    const maxNudges = typeof step.maxNudges === "number" ? step.maxNudges : 3;
    if (nudgeCountRef.current >= maxNudges) {
      setHelpAffordance(true);
      return;
    }

    const baseIdle =
      typeof step.idleSeconds === "number" ? step.idleSeconds : 10;
    const renudge =
      typeof step.renudgeSeconds === "number" ? step.renudgeSeconds : baseIdle;
    // Backoff: first wait = idleSeconds; then renudgeSeconds * 2^(nudgeCount-1)
    const waitSeconds =
      nudgeCountRef.current === 0
        ? baseIdle
        : renudge * 2 ** (nudgeCountRef.current - 1);

    idleTimerRef.current = setTimeout(() => {
      if (dismissedRef.current || !visibleRef.current) return;
      if (hasOtherVoiceSession(PAGE_GUIDE_SESSION_ID)) {
        scheduleNextIdle();
        return;
      }
      const current = getActiveStep();
      if (!current) return;
      const max = typeof current.maxNudges === "number" ? current.maxNudges : 3;
      if (nudgeCountRef.current >= max) {
        setHelpAffordance(true);
        return;
      }
      const nextCount = nudgeCountRef.current + 1;
      setNudgeCount(nextCount);
      nudgeCountRef.current = nextCount;
      void playNudge(current.nudgeText).then(() => {
        if (nextCount >= max) setHelpAffordance(true);
        else scheduleNextIdle();
      });
    }, waitSeconds * 1000);
  }, [clearIdleTimer, getActiveStep, masterOk, playNudge, talkBackActive]);

  const markInteraction = useCallback(() => {
    lastInteractionRef.current = Date.now();
    if (masterOk && !talkBackActive) {
      scheduleNextIdle();
    }
  }, [masterOk, scheduleNextIdle, talkBackActive]);

  // Global interaction resets idle
  useEffect(() => {
    if (!masterOk) {
      clearIdleTimer();
      return undefined;
    }
    const onInteract = () => markInteraction();
    window.addEventListener("pointerdown", onInteract, { passive: true });
    window.addEventListener("keydown", onInteract);
    window.addEventListener("touchstart", onInteract, { passive: true });
    scheduleNextIdle();
    return () => {
      window.removeEventListener("pointerdown", onInteract);
      window.removeEventListener("keydown", onInteract);
      window.removeEventListener("touchstart", onInteract);
      clearIdleTimer();
    };
  }, [masterOk, markInteraction, scheduleNextIdle, clearIdleTimer]);

  // Visibility
  useEffect(() => {
    const onVis = () => {
      visibleRef.current = isPageVisible();
      if (!visibleRef.current) {
        clearIdleTimer();
        stopAudio();
      } else if (masterOk) {
        scheduleNextIdle();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [clearIdleTimer, masterOk, scheduleNextIdle, stopAudio]);

  // Complete step when expectedEvent fires on selector
  useEffect(() => {
    if (!masterOk || !config?.steps?.length) return undefined;
    const step = config.steps[activeStepIndex];
    if (!step?.selector) return undefined;

    const eventName = step.expectedEvent || "click";
    const handler = (event) => {
      const el = document.querySelector(step.selector);
      if (!el) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target === el || el.contains(target) || target.closest?.(step.selector)) {
        stopAudio();
        setCaption("");
        const next = activeStepIndex + 1;
        if (next >= config.steps.length) {
          setActiveStepIndex(config.steps.length);
          clearIdleTimer();
        } else {
          setActiveStepIndex(next);
          setNudgeCount(0);
          nudgeCountRef.current = 0;
          setHelpAffordance(false);
        }
      }
    };

    document.addEventListener(eventName, handler, true);
    return () => document.removeEventListener(eventName, handler, true);
  }, [masterOk, config, activeStepIndex, stopAudio, clearIdleTimer]);

  const dismissForPage = useCallback(() => {
    setDismissedOnPage(true);
    stopAudio();
    clearIdleTimer();
    setCaption("");
    releaseVoiceSession(PAGE_GUIDE_SESSION_ID);
  }, [clearIdleTimer, stopAudio]);

  const enableSound = useCallback(() => {
    unlockPageGuideAudio();
    setSoundBlocked(false);
    const step = getActiveStep();
    if (step?.nudgeText) void playNudge(step.nudgeText);
  }, [getActiveStep, playNudge]);

  const requestTalkBack = useCallback(() => {
    if (hasOtherVoiceSession(PAGE_GUIDE_SESSION_ID)) {
      setTalkBackError("Another voice feature is active on this page.");
      return;
    }
    setTalkBackError("");
    setIosHeadphoneWarning(/iPhone|iPad|iPod/i.test(navigator.userAgent));
    if (isStandalonePwa()) {
      // Prefer text fallback if Web Speech is historically broken in iOS PWA.
      const SpeechRecognition =
        window.SpeechRecognition || window.webkitSpeechRecognition;
      if (!SpeechRecognition) {
        setTextFallbackOpen(true);
        return;
      }
    }
    setMicPrimingOpen(true);
  }, []);

  const activeStep = config?.steps?.[activeStepIndex] || null;

  return {
    loading,
    loadError,
    platformEnabled,
    offered,
    config,
    introductionText,
    introPlaying: Boolean(ctx?.introPlaying),
    playIntroduction,
    stopIntroduction,
    activeStep,
    activeStepIndex,
    caption,
    setCaption,
    soundBlocked,
    enableSound,
    helpAffordance,
    dismissedOnPage,
    dismissForPage,
    masterOk,
    micPrimingOpen,
    setMicPrimingOpen,
    talkBackActive,
    setTalkBackActive,
    talkBackError,
    setTalkBackError,
    textFallbackOpen,
    setTextFallbackOpen,
    iosHeadphoneWarning,
    setIosHeadphoneWarning,
    requestTalkBack,
    playNudge,
    stopAudio,
    pageContext: config?.pageContext || "",
    isStandalonePwa: isStandalonePwa(),
    PAGE_GUIDE_SESSION_ID,
  };
}
